<template>
  <div>
    <BaseError
      v-if="quoteError"
      :class="{'quoteError': quoteError}"
      @reload="onReload()"
    />
    <BaseNewForm v-else>
      <template v-slot:header>
        {{$t('qic_quote_header')}}
      </template>
      <div class="quote">
        <QuoteRenewInformation
          :productName="carProductName"
          :productType="productType"
          :policyNumber="carInfo.policyNumber"
          :quoteNumber="quoteNumber"
          :productTitle="carProductTitle"
          :startPolicyDate="startMotorQuoteDate"
          :endPolicyDate="endMotorQuoteDate"
          :upgrade="isUpgrade">
          <template v-slot:info>
            <li class="quote__info-list_bottom-count">
              <span>{{ $t('renew_car_info_seats') }}: {{ carInfo.seats }}</span>
              <span>{{ $t('renew_car_info_cylinders') }}: {{ carInfo.cylinders }}</span>
            </li>
            <li>{{$t('quote_car_info_shape')}}: {{ carInfo.shape && carInfo.shape.name }}</li>
            <li>{{$t('quote_car_info_reg_year')}}: {{ carInfo.regYear }}</li>
          </template>
        </QuoteRenewInformation>
        <QuoteInclusive
          :quoteInclusivePrice="price"
          :priceWithoutDiscount="planDiscount &&  fullPlanPrice"
          :includes="included"
          :discountsArray="planDiscounts"
          :quoteDiscount="planDiscountPercent"
          :deductibleValue="deductibleValue"
          :quoteAdditionalPrice="fullAdditionalPrice"/>
        <QuoteMotorInfo
          :title="productInfoName"
          :plateNumber="carInfo.plateNumber"
          :plateType="carInfo.type && carInfo.type.name"
          :chassisNumber="carInfo.chassis"/>
        <QuotePolicyOwner
          :fullName="userInfo.fullName"
          :qid="userInfo.qid"
          :email="userInfo.email"
          :phone="userInfo.mobileNumber">
        </QuotePolicyOwner>
        <QuoteAdditionals
          v-if="additionals.length"
          :additionals="additionals"
          :selectedAdditionals="selectedAdditionals"
          :quoteAdditionalPrice="fullAdditionalPrice"
          @changeSelectedAdditionals="onChangeAdditionals">
          <template #accordion-text v-if="ramadanIsActive">
            <RamadanBadge count="+1" position="start" style="margin-block: 5px"/>
          </template>
        </QuoteAdditionals>
        <QuoteCheckout
          :quoteInclusivePrice="fullPlanPrice"
          :quoteTotalPrice="paymentPrice"
          :totalPriceLoading="totalPriceLoading"
          :quoteDiscountOnline="planDiscount"
          :quoteAdditionalPrice="fullAdditionalPrice"
          :paymentPlanSwitch="isSwitcherPaymentPlanOn"
          :promocode="promocode"
          :discountsArray="planDiscounts"
          :quoteNumber="quoteNumber"
          :termsHref="termsLink"
          :cyberAddon="cyberAddonPrice"
          @applePaySucceed="succeedApplePay"
          :getApplePayTotalCallback="getTotalPriceApplePay.bind(this)"
          @onChangePaymentPlan="onChangePlan"
          @onPayment="onSubmit"
          @chooseCard="onChooseCard"
          @setQuoteError="setQuoteError">
          <template #after-total-price v-if="ramadanIsActive">
            <RamadanTicketsBalance />
          </template>
        </QuoteCheckout>
      </div>
    </BaseNewForm>
  </div>
</template>

<script>
import prodCodeMap from '@/assets/maps/prodCodeMap.json'
import { debounce } from '../../utils/debounce'
import { trackGtag } from '@/utils/useGtag'

import {
  QuoteInclusive,
  QuoteAdditionals,
  QuoteCheckout,
  BaseNewForm,
  QuoteRenewInformation,
  QuotePolicyOwner,
  QuoteMotorInfo,
  wizardStepMixin,
  BaseError
} from 'qic-components'
import { getErrorMessage } from '@/shared/errors/getErrorMessage'
import RamadanTicketsBalance from '@/components/campaigns/Ramadan/ui/RamadanTicketsBalance.vue'
import RamadanBadge from '@/components/campaigns/Ramadan/ui/RamadanBadge.vue'
import { mapMutations, mapState } from 'vuex'

export default {
  name: 'PaymentComprehensiveQuote',
  components: {
    RamadanBadge,
    RamadanTicketsBalance,
    QuoteInclusive,
    QuoteAdditionals,
    QuoteCheckout,
    BaseNewForm,
    QuoteRenewInformation,
    QuotePolicyOwner,
    QuoteMotorInfo,
    BaseError
  },
  mixins: [
    wizardStepMixin
  ],
  data () {
    return {
      quoteError: false,
      productType: this.$store.state.renew.productType || '',
      policyInfo: this.$store.state.renew.policyInfo || {},
      selectedAdditionals: [],
      paymentPlan: 'annual',
      additionals: [],
      included: [],
      quote: null,
      totalPrice: null,
      pricePerMonth: null,
      totalPriceLoading: false,
      carShapes: null,
      plateTypes: null,
      quoteNumber: null,
      choosenCard: 'credit-card',
      termsLink: null,

      carodtcLink: null,
      carinstalltcLink: null,
      cartpltcLink: null,
      boadodtcLink: null,
      boattpltcLink: null,
      hometcLink: null
    }
  },
  computed: {
    ...mapState({
      ramadanIsActive: state => state.ramadan.ramadanIsActive,
      ramadanPlanCount: state => state.ramadan.ramadanPlanCount
    }),
    cyberAddonPrice () {
      if (this.selectedAdditionals.findIndex(item => item === '16001') > 0) {
        return Number(this.additionals.find((addon) => addon.id === '16001').value) || 0
      }

      return 0
    },
    docName () {
      if (this.productType === 'car' || this.productType === 'moto') {
        if (this.carProductName === 'Car Comprehensive') {
          if (this.paymentPlan === 'annual') {
            return 'carodtc'
          } else {
            return 'carinstalltc'
          }
        } else {
          return 'cartpltc'
        }
      } else if (this.productType === 'boat') {
        if (this.carProductName === 'Car Comprehensive') {
          return 'boadodtc'
        } else {
          return 'boattpltc'
        }
      } else if (this.productType === 'home') {
        return 'hometc'
      }

      return ''
    },
    price () {
      return Number(this.quote?.price) || 0
    },
    paymentPrice () {
      return this.paymentPlan === 'annual' ? this.totalPrice : this.pricePerMonth
    },
    discountPercent () {
      return this.planDiscount ? 10 : 0
    },
    planDiscount () {
      if (this.quote?.discount) {
        return Number(this.quote?.discount)
      }
      return 0
    },
    planDiscountPercent () {
      if (this.planDiscount) return (this.planDiscount * 100 / this.fullPlanPrice).toFixed(0)
      return 0
    },
    changePlanInfo () {
      return this.$store.state.motor.changePlan
    },
    planDiscounts () {
      if (this.quote?.discounts) {
        return this.quote?.discounts
      }
      return []
    },
    fullPlanPrice () {
      return this.price + this.planDiscount
    },
    inclusiveCovers () {
      return this.included.reduce((prev, item) => prev + Number(item.value), 0)
    },
    submitDisabled () {
      return !this.acceptance
    },
    fullAdditionalPrice () {
      return this.additionals.reduce((acc, add) => {
        if (this.selectedAdditionals.includes(add.id)) return acc + Number(add.value)
        return acc
      }, 0)
    },
    deductibleValue () {
      const deductible = this.quote?.deductible
      return deductible ? Number(deductible) : null
    },
    productStaticIncludes () {
      const includes = this.productInfo?.staticIncludes ? [...this.productInfo?.staticIncludes] : []
      return includes
        .map(i => {
          i.name = this.$t(i.name)
          i.description = this.$t(i.description)
          return i
        })
    },
    productInfo () {
      return prodCodeMap
        .filter(prod => prod.type === this.productType)
        .find(prod => prod.code === (this.changePlanInfo.status &&
        this.changePlanInfo?.prodCode
          ? this.changePlanInfo?.prodCode
          : this.policyInfo?.prodCode))
    },
    disabledProductAdditionals () {
      return this.productInfo?.disabledAdditionals || []
    },
    isSwitcherPaymentPlanOn () {
      return this.productInfo?.monthlyPayment || false
    },
    locale () {
      return this.$i18n.locale
    },
    carInfo () {
      if (!this.policyInfo) return {}
      const curShape = this.carShapes && this.carShapes.find(s => s.code === this.policyInfo.vehicleShape)
      const curType = this.plateTypes && this.plateTypes.find(s => s.code === this.policyInfo.plateCharacter)
      return {
        makeModel: this.policyInfo.makeModel,
        modelYear: this.policyInfo.modelYear,
        policyNumber: this.policyInfo.policyNo,
        chassis: this.policyInfo.chassisNo,
        cylinders: this.policyInfo.noOfCylinder,
        seats: this.policyInfo.seatingCapacity,
        regYear: this.policyInfo.regYear,
        shape: {
          id: this.policyInfo.vehicleShape,
          name: this.locale === 'ar' ? curShape?.descAr : curShape?.desc
        },
        type: {
          id: this.policyInfo.plateCharacter,
          name: this.locale === 'ar' ? curType?.descAr : curType?.desc
        },
        plateNumber: this.policyInfo.regNo
      }
    },
    carProductTitle () {
      return `${this.carInfo.makeModel} ${this.carInfo.modelYear}`
    },
    productInfoName () {
      return this.$store.getters.productInfoName
    },
    carProductName () {
      if (!this.policyInfo) return ''
      if (this.productType === 'moto') return this.$t('renew_moto_insurance')
      if (this.changePlanInfo.status) {
        return this.changePlanInfo.prodCode === '0110' ? this.$t('renew_car_comprehensive') : this.$t('renew_car_tpl')
      }
      return this.policyInfo.prodCode === '0110' ? this.$t('renew_car_comprehensive') : this.$t('renew_car_tpl')
    },
    userInfo () {
      if (!this.policyInfo) return {}
      return {
        fullName: this.policyInfo.insName,
        qid: this.policyInfo.civilId,
        mobileNumber: !this.policyInfo.mobileNo
          ? ''
          : (this.policyInfo.mobileNo.length === 8)
              ? `+974${this.policyInfo.mobileNo}`
              : `+${this.policyInfo.mobileNo}`,
        email: this.policyInfo.emailId
      }
    },
    promocode () {
      return this.$store.state.motor?.promocode || ''
    },
    startMotorQuoteDate () {
      if (!this.policyInfo.polEndDate) return new Date()
      const endDate = new Date(this.policyInfo.polEndDate)
      const cur = new Date()
      return endDate < cur ? cur : new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1)
    },
    endMotorQuoteDate () {
      const start = this.startMotorQuoteDate
      return new Date(start.getFullYear() + 1, start.getMonth(), start.getDate() - 1)
    },
    isUpgrade () {
      const changePlanInfo = this.$store.state.motor.changePlan
      const odCode = '0110'
      return changePlanInfo.status && changePlanInfo.prodCode === odCode
    }
  },
  methods: {
    ...mapMutations([
      'setRamadanPlanCount',
      'setRamadanTicketsCount'
    ]),
    setQuoteError (is) {
      this.quoteError = is
    },
    onReload () {
      location.reload()
    },
    onChooseCard (cardData) {
      this.choosenCard = cardData.paymentMethod
    },
    async onSubmit ({ paymentMethod }) {
      try {
        await this.$store.dispatch('updateMotorPolicy', this.quoteNumber)
      } catch (err) {
        throw (typeof err === 'string') ? new Error(err) : err
      }

      const productVariant = this.carProductName === 'Car Comprehensive' ? 'OD' : 'TPL'
      const eventData = {
        event: 'purchase',
        coupon: this.promocode,
        transaction_id: this.quoteNumber,
        policy_id: this.policyInfo.policyNo,
        number_of_addons: this.selectedAdditionals.length,
        plan_name: productVariant,
        total_price: this.paymentPrice,
        choosen_card: this.choosenCard
      }

      localStorage.setItem('eventData', JSON.stringify(eventData))

      trackGtag('complete_purchase', {
        page: 'your_quote',
        coupon: this.promocode,
        transaction_id: this.quoteNumber,
        policy_id: this.policyInfo.policyNo,
        number_of_addons: this.selectedAdditionals.length,
        plan_name: productVariant,
        total_price: this.paymentPrice,
        choosen_card: this.choosenCard,
        glm: this.quote.glmScheme
      })

      this.loadNextPage(
        this.$store.dispatch('redirectMotorToRenewPayment', {
          ids: this.selectedAdditionals,
          paymentPlan: this.paymentPlan,
          paymentMethod,
          deductibles: {
            ...(this.quote.glmScheme && { deductibleValue: this.quote.deductible }),
            ...(this.quote.glmScheme && { deductibleCode: this.quote.deductibleCode })
          }
        })
          .then(() => {
            this.$store.commit('saveLocale', this.$i18n.locale)
          })
      )
    },
    updateTotalPrice () {
      this.totalPriceLoading = true
      return this.$store.dispatch('getMotorRenewPrice', {
        ids: this.selectedAdditionals,
        selectproduct: false,
        ...(this.quote.glmScheme && { deductibleValue: this.quote.deductible }),
        ...(this.quote.glmScheme && { deductibleCode: this.quote.deductibleCode })
      })
        .then(({ totalPrice, perMonth }) => {
          this.totalPrice = totalPrice
          this.pricePerMonth = perMonth
        })
        .catch((error) => {
          this.emitError(getErrorMessage(error))
        })
        .finally(() => {
          this.totalPriceLoading = false
        })
    },
    onChangeAdditionals ({ newAdditionals }) {
      const removedAdditionals = this.selectedAdditionals.filter(id => !newAdditionals.includes(id))
      const addedAdditionals = newAdditionals.filter(id => !this.selectedAdditionals.includes(id))
      const productVariant = this.carProductName === 'Car Comprehensive' ? 'OD' : 'TPL'

      removedAdditionals.forEach(removedAdditionalId => {
        const removedAdditional = this.additionals.find(additional => additional.id === removedAdditionalId)

        if (removedAdditional) {
          const ecommerceData = {
            items: [
              {
                item_name: removedAdditional.name,
                price: removedAdditional.value,
                item_category: 'Car Insurance',
                item_variant: productVariant
              }
            ]
          }
          trackGtag('remove_from_cart', {
            ecommerce: ecommerceData
          })
        }
      })

      addedAdditionals.forEach(addedAdditionalId => {
        const addedAdditional = this.additionals.find(additional => additional.id === addedAdditionalId)
        if (addedAdditional) {
          const ecommerceData = {
            items: [
              {
                item_name: addedAdditional.name,
                price: addedAdditional.value,
                item_category: 'Car Insurance',
                item_variant: productVariant
              }
            ]
          }
          trackGtag('add_to_cart', {
            ecommerce: ecommerceData
          })
        }
      })
      this.selectedAdditionals = newAdditionals
      this.setRamadanTicketsCount(this.ramadanPlanCount + this.countSelectedAddonsLength())
      this.updateTotalPriceDebounce()
    },
    onChangePlan (plan) {
      this.paymentPlan = plan
    },
    selectAllAdditionals () {
      this.additionals.forEach((add) => {
        this.selectedAdditionals.push(add.id)
      })
    },
    getTotalPriceApplePay () {
      return this.$store.dispatch('getTotalPrice', {
        quoteno: this.quoteNumber,
        schemecode: this.quote.schemecode,
        productcode: this.quote.productcode,
        selectproduct: true
      })
        .then((res) => {
          return res
        })
    },
    countSelectedAddonsLength () {
      try {
        return this.additionals.filter(obj => this.selectedAdditionals.includes(obj.id)).length
      } catch (e) {
        return 0
      }
    },
    succeedApplePay () {
      this.$router.replace({ name: 'PaymentInsuranceSuccess' })
    },
    updateTotalPriceDebounce () {},
    async getTermsLink (docname = this.docName) {
      const { data } = await this.$store.dispatch('getQatarWordings', { docname })

      if (data?.Wordings.length > 1) {
        return data?.Wordings.map((wording) => wording.Link)
      }

      if (!data?.Wordings[0].length) {
        return data?.Wordings[0].Link
      }
    },
    async loadTermsLink () {
      if (this.docName === 'carodtc' || this.docName === 'carinstalltc') {
        if (!this.carodtcLink || !this.carinstalltcLink) {
          const [carodtc, carinstalltc] = await this.getTermsLink('carodtc,carinstalltc')
          this.carodtcLink = carodtc
          this.carinstalltcLink = carinstalltc
        }

        if (this.docName === 'carodtc') {
          this.termsLink = this.carodtcLink
        }
        if (this.docName === 'carinstalltc') {
          this.termsLink = this.carinstalltcLink
        }
      }

      if (this.docName === 'cartpltc') {
        if (!this.cartpltcLink) this.cartpltcLink = await this.getTermsLink()
        this.termsLink = this.cartpltcLink
      }

      if (this.docName === 'boadodtc') {
        if (!this.boadodtcLink) this.boadodtcLink = await this.getTermsLink()
        this.termsLink = this.boadodtcLink
      }

      if (this.docName === 'boattpltc') {
        if (!this.boattpltcLink) this.boattpltcLink = await this.getTermsLink()
        this.termsLink = this.boattpltcLink
      }

      if (this.docName === 'hometc') {
        if (!this.hometcLink) this.hometcLink = await this.getTermsLink()
        this.termsLink = this.hometcLink
      }
    },
    initRamadan () {
      const ticketsByPlan = this.carProductName === 'Car Comprehensive' ? 12 : 8
      this.setRamadanPlanCount(ticketsByPlan)
      const addons = this.countSelectedAddonsLength()
      const tickets = ticketsByPlan + addons
      this.setRamadanTicketsCount(tickets)
    }
  },
  beforeMount () {
    this.loadTermsLink()
  },
  watch: {
    docName () {
      this.loadTermsLink()
    }
  },
  mounted () {
    this.$store.dispatch('setUserLoginData')
    setTimeout(() => {
      const productVariant = this.carProductName === 'Car Comprehensive' ? 'OD' : 'TPL'
      const ecommerceData = {
        items: [
          {
            item_name: this.carProductName === 'Car Comprehensive' ? 'Comprehensive Insurance' : 'Third Party Liability',
            price: this.paymentPrice,
            item_category: 'Car Insurance',
            item_variant: productVariant
          }
        ]
      }
      trackGtag('add_to_cart', {
        ecommerce: ecommerceData
      })
    }, 6000)

    // Debounce connect for additional change
    this.updateTotalPriceDebounce = () => {
      this.totalPriceLoading = true
      return debounce(() => this.updateTotalPrice(), 600)()
    }
    this.load(
      Promise.all([
        this.$store.dispatch('getMotorRenewQuote'),
        this.$store.dispatch('getMotorShapesDependOnVehicle', this.policyInfo?.vehicleTypeCode),
        this.$store.dispatch('getMotorPlateTypes')
      ])
        .then(([quote, carShapes, plateTypes]) => {
          this.quote = quote.scheme
          this.quoteNumber = quote.quoteNumber
          this.carShapes = carShapes
          this.plateTypes = plateTypes
          this.additionals = this.quote.ids
            .map(add => ({ ...add, loader: false }))
            .filter(add => !this.disabledProductAdditionals.includes(add.id))
          const staticIncludes = this.productStaticIncludes.filter(s => !this.quote.included.find(include => include.id === s.id))
          this.included = [...staticIncludes, ...this.quote.included]

          const motorUpgradeSchemes = this.$store.state.motor.motorUpgradeSchemes.info.schemes

          if (this.quote.glmScheme && motorUpgradeSchemes && motorUpgradeSchemes.length) {
            const foundScheme = motorUpgradeSchemes.find(scheme => scheme.price === this.quote.price)

            if (!foundScheme) {
              this.quoteError(true)
            }
          }

          const policyInfoCoverCodes = this.$store.state.renew.policyInfo.coversList.map(i => i.coverCode)
          const additionalsIds = [...this.quote.included.map(i => i.id), ...this.quote.ids.map(i => i.id)]

          this.selectedAdditionals = additionalsIds.filter(id => policyInfoCoverCodes.includes(id))

          return this.updateTotalPrice()
        })
        .then(this.initRamadan)
    )
  }
}
</script>

<style lang="scss">
.quoteError {
  margin-top: 0px;
}
</style>
